// 歷史經期紀錄 —— **就地編輯 / 刪除**（W58.5）。
//
// 使用者的反饋（審計 §2.3 的①）：
//
// > **「若後端支援修改或刪除單筆歷史經期紀錄（如 `10/6 - 10/10`）…前端必須
// > 提供符合直覺的編輯/刪除入口與確認機制。」**
//
// **審計找到的洞。** `GET /calendar/cycle` 的 `history[]` 每一筆都帶著
// `event_id`（後端早就給了），而 `PATCH /calendar/events/{id}` 與
// `DELETE /calendar/events/{id}` 兩條路都在 —— 但週期頁的歷史清單是一個
// **沒有任何互動的 `<li>`**。使用者看到 `10/06 – 10/10`，想改日期或刪掉，
// 只能自己去月曆上找那一天。
//
// **兩個動作，兩條既有路：**
//
// - **編輯** → `/calendar/events/{event_id}/edit` —— 既有路由、既有表單、
//   既有「自動套用經期長度」的邏輯。**不重做一份表單。**
// - **刪除** → `DELETE` + **撤銷條**（8 秒）→ `POST .../restore`。
//   與 `CalendarEventDetail` 完全同一套（`useUndoBar` + `UndoBar`）。
//
// **不做「刪除後就看不到這筆安排了，此操作無法復原」那種假的不可逆文案**
// （ADR-0080）—— 那是軟刪除，有 7 天復原窗。

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { UndoBar } from "@/components/patterns/UndoBar";
import { useUndoBar } from "@/hooks/useUndoBar";
import { CATEGORY_ICON } from "@/components/calendar/calendarIcons";
import { deleteEvent, restoreEvent, type CycleHistoryEntry } from "@/lib/api/calendar";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";

export interface CycleHistoryListProps {
  history: readonly CycleHistoryEntry[];
}

export function CycleHistoryList({ history }: CycleHistoryListProps) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const undo = useUndoBar<{ id: string }>();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteEvent(id),
    onSuccess: async (_data, id) => {
      await qc.invalidateQueries({ queryKey: ["calendar-cycle"] });
      await qc.invalidateQueries({ queryKey: ["calendar-month"] });
      await qc.invalidateQueries({ queryKey: ["calendar-day"] });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      undo.begin({ id });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => restoreEvent(id),
    onSuccess: async () => {
      undo.clear();
      await qc.invalidateQueries({ queryKey: ["calendar-cycle"] });
      await qc.invalidateQueries({ queryKey: ["calendar-month"] });
      await qc.invalidateQueries({ queryKey: ["calendar-day"] });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  if (history.length === 0) return null;

  return (
    <section className="flex flex-col gap-s-2">
      <h2 className="text-micro uppercase tracking-wide text-ink-3">
        歷史記錄 · {history.length} 次
      </h2>
      <ul
        className="flex flex-col divide-y divide-hairline rounded-md border border-hairline"
        data-testid="calendar-cycle-history"
      >
        {history.map((h) => (
          <li key={h.event_id} className="flex items-center gap-s-3 p-s-3">
            {/* W58.0 — the chip is **the same chip the month grid draws**: 18px
                box, 13px glyph. It used to be a 15px box with an 11px `Droplet`,
                and 11px is not a size at which a flower reads as a flower. The
                `Droplet` also went — `CATEGORY_ICON.cycle` is the single source
                for what a cycle looks like, and it is a flower now (W58.5: a
                five-petal silhouette, ADR-0209). */}
            <span
              className={cn(
                "flex h-[var(--chip-h)] w-[var(--chip-h)] shrink-0 items-center justify-center rounded-[6px]",
                "bg-partner-tint text-partner-strong",
              )}
              aria-hidden="true"
            >
              {(() => {
                const Icon = CATEGORY_ICON.cycle;
                return <Icon size={13} aria-hidden="true" />;
              })()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body-sm tabular-nums text-ink-1">
                {fmt(h.start_date)}
                {h.end_date ? ` – ${fmt(h.end_date)}` : ""}
              </p>
              <p className="mt-s-1 flex items-center gap-s-2 text-meta text-ink-3">
                <span>由{h.recorded_by_name}記錄</span>
                <span>{h.days} 天</span>
              </p>
            </div>
            {/* W58.5 — **就地編輯 / 刪除。** 44px 的觸控目標（與日詳情的
                「查看 / 編輯」同一個形狀），而刪除走撤銷條，所以不需要
                確認框 —— 確認框是給**不可逆**的動作的（ADR-0201）。 */}
            <div className="flex shrink-0 items-center">
              <button
                type="button"
                className="min-h-11 min-w-11 px-s-1 -mx-s-1 text-meta font-semibold text-you-strong hover:underline"
                onClick={() => nav(`/calendar/events/${h.event_id}/edit`)}
                aria-label={`編輯 ${fmt(h.start_date)} 的經期紀錄`}
                data-testid={`calendar-cycle-history-edit-${h.event_id}`}
              >
                編輯
              </button>
              <button
                type="button"
                className="min-h-11 min-w-11 px-s-1 -mx-s-1 text-meta font-semibold text-ink-2 hover:underline disabled:opacity-50"
                onClick={() => deleteMutation.mutate(h.event_id)}
                disabled={deleteMutation.isPending}
                aria-label={`刪除 ${fmt(h.start_date)} 的經期紀錄`}
                data-testid={`calendar-cycle-history-delete-${h.event_id}`}
              >
                刪除
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/*
        R1 (ADR-0080) — 撤銷條。與 `CalendarEventDetail` 同一個位置
        （`fixed` + 108px），因為**浮動底欄的島在 108px 以下** —— 一個讀不到的
        撤銷條是這個模式唯一不能有的失敗。
      */}
      {undo.item ? (
        <div
          data-testid="calendar-cycle-undo-slot"
          className="pointer-events-none fixed inset-x-0 bottom-[108px] z-30 flex justify-center px-4"
        >
          <div className="pointer-events-auto relative w-full max-w-md">
            <UndoBar
              open
              message="經期紀錄已刪除"
              onUndo={() => restoreMutation.mutate(undo.item!.id)}
              className="inset-x-0"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function fmt(d: string | null | undefined): string {
  if (!d) return "—";
  return `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
}
